<template>
  <el-drawer v-model="is_show_drawer" size="100%" class="collection-detail-drawer" :destroy-on-close="true">
    <header class="head" :key="collection.is_product_changed" v-loading="$store.state.spec_value.storefrontLoading">
      <i @click="close"><svg-icon name="close2" /></i>
      <div class="actions" v-if="!is_loading_data" :key="collection.product_count">
        <el-button
          type="primary"
          v-if="collection.status !== STATUS_CODE.Active"
          class="ui-button"
          @click="publishBtn"
          :disabled="!collection.product_count || !canClickItem"
          >{{ 'Publish' }}</el-button
        >
        <el-button
          :type="[collection.is_product_changed ? 'normal' : 'primary']"
          v-else
          class="ui-button"
          @click="publishBtn"
          >Disable</el-button
        >

        <el-button
          type="primary"
          v-if="collection.status == STATUS_CODE.Active && collection.is_product_changed"
          class="ui-button"
          @click="publishBtn('is_pub')"
          >Publish change</el-button
        >
      </div>
    </header>
    <div class="info" :key="collection_id" v-if="!is_loading_data && !$store.state.spec_value.storefrontLoading">
      <div class="image-wrap" v-if="collection.cover_image_url || collection.first_4_product_thumbnails">
        <collection-images :image="collection.cover_image_url" :images="collection.first_4_product_thumbnails" />
      </div>
      <div class="meta">
        <h2 v-html="collection.title" v-if="collection.title"></h2>
        <h2 v-else>- - -</h2>
        <p v-if="collection.description" v-html="collection.description"></p>
        <ul>
          <li>
            <span>By {{ collection.operator_name }}</span>
          </li>
          <li>
            <svg-icon name="bag" />
            <span :key="collection.product_count">{{ collection.product_count || '0' }}</span>
          </li>
          <li v-if="collection.status === STATUS_CODE.Active && collection.published_at">
            <svg-icon name="calendar" />
            <span
              >{{ formatDate(collection.published_at)
              }}{{ collection.disable_datetime ? ` - ${formatDate(collection.disable_datetime)}` : '' }}</span
            >
          </li>
          <li v-else-if="collection.publish_datetime && collection.status !== STATUS_CODE.Disabled">
            <svg-icon name="calendar" />
            <span
              >{{ formatDate(collection.publish_datetime)
              }}{{ collection.disable_datetime ? ` - ${formatDate(collection.disable_datetime)}` : '' }}</span
            >
          </li>

          <li v-else-if="collection.status === STATUS_CODE.Disabled && collection.disabled_at">
            <svg-icon name="calendar" />
            <span :key="collection.total_view_count"
              >{{ collection.published_at ? formatDate(collection.published_at) : '---' }} -
              {{ collection.disabled_at ? formatDate(collection.disabled_at) : '---' }}</span
            >
          </li>

          <li>
            <Badage v-if="collection.is_feature" :value="'Featured'" :type="'yellow'" />
          </li>
          <li>
            <Badage :value="collection?.statusBag?.label" :type="collection?.statusBag?.color" />
          </li>
          <li>
            <el-button class="edit-btn" @click="editCollection">Edit<svg-icon name="pen" /></el-button>
          </li>
        </ul>
      </div>
    </div>

    <div class="products-box" v-loading="is_loading || $store.state.spec_value.storefrontLoading">
      <h3 v-if="products.length > 0">All products</h3>
      <drop-list
        class="item-list"
        :items="products"
        :class="{ 'is-sorting': is_sorting }"
        @reorder="$event.apply(products)"
        v-loading="is_update_data"
      >
        <template v-slot:item="{ item, index }">
          <drag
            :key="item.id"
            :scrolling-edge-size="-1000"
            handle=".drag-handle"
            @dragstart="dragStart(index, item)"
            @dragend="dragEnd(index, item)"
          >
            <product-item
              :item="item"
              :is_in_collection="true"
              @delete-product="deleteProducts"
              :is_disabled_content="is_disabled"
              :collection="collection"
            />
          </drag>
        </template>

        <template v-slot:feedback> feedback </template>
      </drop-list>
      <div v-if="!is_loading && products.length === 0 && is_end" class="empty">
        <svg-icon name="bag" />
        <h3>Your collection is currently empty</h3>
        <p>
          Add products to get started.<br />
          Note: Collections that are empty won't be displayed publicly.
        </p>
      </div>
      <div class="empty" v-else-if="!is_loading && is_end && products.length !== 0">No more products</div>

      <div class="load-more" v-else-if="!is_end && !is_loading" v-loading="is_loading_more">
        <el-button type="primary" @click="getData(true)" v-if="!is_loading_more" size="large"> Load more </el-button>
      </div>
    </div>
    <manage-tools
      v-if="!is_loading_data && !is_disabled"
      @open-add="tagProductsToCollection"
      @open-manage="emit('open-manage', collection)"
      :collection_row:="collection"
      :is_empty="is_loading || (!is_loading && products.length === 0 && is_end)"
    />
  </el-drawer>
  <confirm-dialog ref="confirm_dialog_el" :data="confirm_data" />

  <el-dialog
    v-model="alert_isChange_dialog"
    class="ui-alert-change-dialog"
    :close-on-click-modal="false"
    :show-close="false"
    width="350"
  >
    <h2>Changes saved. Publish now?</h2>
    <div slot="footer" class="dialog-footer">
      <el-button
        type="text"
        @click="
          alert_isChange_dialog = false;
          close('closeYes');
        "
        >Cancel</el-button
      >
      <el-button type="primary" @click="publishBtn('is_pub')">Publish</el-button>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import CollectionImages from './CollectionImages.vue';
import ProductItem from './ProductItem.vue';
import ManageTools from './ManageTools.vue';
import { Drag, DropList } from 'vue-easy-dnd';
import ConfirmDialog from './ConfirmDialog.vue';
import { ElDrawer, ElButton, ElDialog } from 'element-plus';
import Badage from '@/components/ui/Badage.vue';
import moment from 'moment';
import { useStorefrontFunction } from '@/components/storefront/StorefrontFunction.js';
import { useStore } from 'vuex';

const { STATUS_CODE, handleStatus } = useStorefrontFunction();
const store = useStore();
const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const event = app.appContext.config.globalProperties.EventBus;
const emit = defineEmits(['share', 'feature', 'open-manage', 'edit', 'publish', 'delete', 'move']);

const props = defineProps({
  merchant_account_id: {
    type: Number,
    default: 0,
  },
  merchant_display_name: {
    type: String,
    default: '',
  },
  tag_type: {
    type: String,
    default: '',
  },
});

const is_show_drawer = ref(false);
const is_loading = ref(false);
const is_loading_data = ref(false);
const is_update_data = ref(false);
const confirm_dialog_el = ref(null);
const confirm_data = ref({
  title: '',
  description: '',
  action: () => {},
});
const collection_id = ref(0);
const collection = ref({});
const products = ref([]);
const is_end = ref(false);
const page = ref(1);
const is_loading_more = ref(false);
const is_sorting = ref(false);
const is_disabled = computed(
  () =>
    collection.value.contentModertaion?.status === 70 ||
    (collection.value.contentModertaion?.status === 65 &&
      collection.value.contentModerationStatusHistory?.is_creator_response_message) ||
    (collection.value.contentModerationFlagHistory !== null &&
      collection.value.contentModertaion?.flag_status !== 0 &&
      collection.value.contentModerationFlagHistory?.is_creator_response_message)
);

let drag_pre_index = -1;

const canClickItem = computed(() => {
  let val = collection.value;
  const hasAny = val.cover_image_url && val.title;

  return hasAny;
});

const alert_isChange_dialog = ref(false);
//+++++++++++++++++++++++++++++++++++++++
const getData = (is_next = false, is_update = false) => {
  if (is_next) {
    page.value++;
    is_loading_more.value = true;
  } else {
    is_loading.value = true;
    page.value = 1;
    products.value = [];
  }

  ajax
    .get(
      `/storefront-curation/get-curation-collection-item-list?curation_collection_id=${collection_id.value}&page=${page.value}`
    )
    .then((res) => {
      res.data.data.data.forEach((item) => {
        products.value.push(item);
      });
      products.value = Array.from(new Map(products.value.map((item) => [item.id, item])).values());
      is_end.value = res.data.data.data.length < 15;
      is_loading.value = false;
      is_loading_more.value = false;
    });

  if (is_update) {
    updateCollection();
  }
};

const updateCollection = () => {
  is_loading.value = true;
  ajax.get(`/storefront-curation/get-curation-collection?curation_collection_id=${collection_id.value}`).then((res) => {
    if (!res.data.data.data[0]) {
      return;
    }
    handleStatus(res.data.data.data[0]);
    const item = res.data.data.data[0];
    collection.value.product_count = item.product_count;
    collection.value.is_product_changed = item.is_product_changed;
    is_loading.value = false;
  });
};

const dragStart = (index, item) => {
  drag_pre_index = index;
  is_sorting.value = true;
};

const dragEnd = (index, item) => {
  is_sorting.value = false;
  collection.value.is_product_changed = 1;
  if (index !== drag_pre_index) {
    emit('move', {
      ids: [item.product_id || item.id],
      direction: drag_pre_index > index ? 'up' : 'down',
      distance: drag_pre_index > index ? drag_pre_index - index : index - drag_pre_index,
      collection_id: collection_id.value,
      type: 'collection-product',
    });
  }

  drag_pre_index = -1;
};

const open = (id = 0) => {
  is_show_drawer.value = true;
  getData();
};

const tagProductsToCollection = () => {
  event.emit('tag-products', {
    id: collection.value.id,
    which: 'collection',
    row: collection.value,
  });
};

const close = (val) => {
  let isActiveWithChange = collection.value.status === STATUS_CODE.Active && collection.value.is_product_changed && products.value.length > 0;
  let isNewAndHadProduct = collection.value.status === STATUS_CODE.Draft && window.localStorage.getItem('storefront_collection_isNew') && products.value.length > 0;
  if (
    (isActiveWithChange || isNewAndHadProduct) &&
    val !== 'closeYes'
  ) {
    alert_isChange_dialog.value = true;
    return;
  } else {
    // remove query on url
    const url = new URL(window.location.href);
    url.searchParams.delete('id');
    // ✅ 完全不刷新
    history.replaceState(null, '', url.pathname + (url.search || ''));
    window.localStorage.removeItem('storefront_collection_detail_row');
    window.localStorage.removeItem('storefront_collection_isNew');
    is_show_drawer.value = false;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return moment(dateStr).format('MMM DD, YYYY');
};

const deleteProducts = (ids, cbStart = null, cbEnd = null) => {
  confirm_data.value = {
    title: `Are you sure you want to remove ${
      ids.length === 1 ? 'this product' : 'these products'
    } from this collection?`,
    description: `You will be removing ${ids.length} product${ids.length === 1 ? '' : 's'}.`,
    action: () => {
      is_update_data.value = true;
      if (cbStart) cbStart();

      is_loading.value = true;
      ajax
        .post('/storefront-curation/remove-curation-collection-item', {
          product_ids: ids,
          curation_collection_ids: [collection_id.value],
        })
        .then((res) => {
          products.value = products.value.filter((item) => !ids.includes(item.product_id));
          is_update_data.value = false;
          if (cbEnd) cbEnd();
          updateCollection();
        });
    },
  };
  confirm_dialog_el.value.open();
};
const editCollection = () => {
  event.emit('open-collection-edit-dialog', { id: 1, row: collection.value, which: 'collection' });
};

const publishBtn = (val) => {
  if (collection.value.status === STATUS_CODE.Active && val !== 'is_pub') {
    //disable
    event.emit('open-storefront-collection-handle-dialog', {
      type: 'Disabled',
      data: [collection.value],
    });
    return;
  } else {
    let type = 'Publish';
    alert_isChange_dialog.value = false;

    if (
      (collection.value.status === STATUS_CODE.Draft || collection.value.status === STATUS_CODE.Disabled) &&
      collection.value.publish_datetime
    ) {
      type = 'PublishDraftToSchedule';
    } else if (collection.value.is_schedule) {
      type = 'PublishSchedule';
    }

    event.emit('open-storefront-collection-handle-dialog', {
      type: type,
      data: [collection.value],
    });
  }
};
//++++++++++++++++++++++++++++++++++
onMounted(() => {
  event.off('updated-collection-info');
  event.on('updated-collection-info', (item) => {
    if (is_show_drawer.value && collection.value.id === collection_id.value) {
      collection.value.name = item.name;
      collection.value.description = item.description;
      collection.value.cover_image_url = item.cover_image_url;
      collection.value.first_4_product_thumbnails = item.first_4_product_thumbnails;
      collection.value.is_publish = item.is_publish;
      collection.value.is_feature = item.is_feature;
    }
  });

  event.off('refresh-tagged-products');
  event.on('refresh-tagged-products', () => {
    console.log('update-tagged-products');
    getData();
  });

  event.off('open-collection-detail');
  event.on('open-collection-detail', async (val) => {
    is_loading.value = true;
    is_loading_data.value = true;
    is_show_drawer.value = true;
    products.value = [];

    const url = new URL(window.location.href);
    if( url.searchParams.get('isNew')==='true'){
      window.localStorage.setItem('storefront_collection_isNew', 'true');
    }

    if (val.content_id) {
      collection_id.value = val.content_id;
    } else {
      collection_id.value = val.id;
    }

    ajax
      .get(`/storefront-curation/get-curation-collection?curation_collection_id=${collection_id.value}`)
      .then((res) => {
        if (!res.data.data.data[0]) {
          close();
          return;
        }

        handleStatus(res.data.data.data[0]);
        const item = res.data.data.data[0];
        item.type = 'collection';
        collection.value = item;
        open(collection.value.id);

        if (item.is_created || val.need_open_pop) {
          setTimeout(() => {
            emit('tag-product', collection.value);
          }, 1000);
        }
      })
      .finally(() => {
        is_loading_data.value = false;
      });
  });

  event.off('delete-products-from-collection');
  event.on('delete-products-from-collection', (obj) => {
    if (is_show_drawer.value && obj.collection_id === collection.value.id) {
      deleteProducts(obj.ids, obj.cbStart, obj.cbEnd);
    }
  });
});

defineExpose({
  open,
  close,
  getData,
  isShow() {
    return is_show_drawer.value;
  },
});
</script>

<style lang="scss">
.collection-detail-drawer {
  .el-drawer__header {
    display: none;
  }

  .head {
    display: flex;
    align-items: center;
    padding: 16px;
    background-color: white;
    position: sticky;
    z-index: 3;
    top: 0;

    i {
      cursor: pointer;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 4px;
      color: var(--text-primary);
      border-radius: var(--radius);
      transition: background-color 0.2s;

      svg {
        width: 18px;
        height: 18px;
      }

      &:hover {
        background-color: var(--Neu-20);
      }
    }

    .actions {
      display: flex;
      margin-left: auto;

      .el-button {
        margin-left: 8px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  .info {
    display: flex;
    align-items: center;
    padding: 0 16px 16px;
    background-color: white;
    overflow: hidden;

    .image-wrap {
      width: 128px;
      margin-right: 16px;
      position: relative;
      overflow: hidden;
      aspect-ratio: 1 / 1;
    }

    .meta {
      flex: 1;

      h2 {
        font: var(--font-med-24);
        color: var(--text-primary);
        margin-bottom: 8px;
      }

      p {
        color: var(--text-primary);
      }

      ul {
        display: flex;
        gap: 16px;
        margin-top: 12px;

        li {
          display: flex;
          align-items: center;
          font: var(--font-med-12);
          color: var(--text-secondary);

          svg {
            width: 16px;
            height: 16px;
            margin-right: 4px;

            &.svg-icon__star-filled {
              fill: var(--yellow);
            }
          }

          .edit-btn {
            svg {
              width: 12px;
              height: 12px;
              margin-left: 8px;
            }
          }
        }
      }
    }
  }

  .products-box {
    padding: 16px 16px 180px;

    h3 {
      font: var(--font-med-16);
      color: var(--text-primary);
      margin-bottom: 16px;
    }

    .item-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;

      @media screen and (max-width: $mobile_size) {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      }

      .dnd-drag {
        // cursor: grab;
        transition: transform 0.2s ease;

        // &.drag-mode-reordering {
        //   cursor: grabbing;
        // }

        .drag-handle {
          cursor: grab;
        }
      }

      &.is-sorting {
        .dnd-drag {
          pointer-events: none;
          opacity: 0.5;

          &.drag-mode-reordering {
            opacity: 1;
          }
        }
      }
    }

    .empty {
      text-align: center;
      padding: 24px;

      svg {
        height: 48px;
        height: 48px;
        fill: var(--black-60);
        margin: auto;
      }

      h3 {
        font: var(--font-med-18);
        color: var(--text-secondary);
        margin: 10px 0;
      }

      p {
        color: var(--text-secondary);
      }
    }

    .load-more {
      text-align: center;
      margin-top: 32px;

      .el-button {
        padding-left: 24px;
        padding-right: 24px;
      }
    }
  }

  .manage-product-item {
    figure {
      border: 1px solid var(--Border-default);
    }

    h3 {
      padding-left: 8px;
      padding-right: 8px;
      margin-bottom: 8px;
    }

    .bot {
      padding-left: 8px;
    }
  }

  .manage-page-tools {
    left: 0;
  }

  .ui-drawer__header {
    display: none;
  }

  .ui-drawer__body {
    padding: 0;
  }
}

.dnd-ghost {
  z-index: 999999999 !important;
}

.ui-alert-change-dialog {
  padding: 24px;
  .el-dialog__header {
    display: none;
  }

  h2 {
    font: var(--font-demi-20);
    color: var(--black-90);
    margin-bottom: 24px;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
